import React, { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import { UPDATE_NOTIFICATION_SETTINGS } from '../graphql/mutations/notificationSettings';
import { usePortfolioContext } from '../contexts/PortfolioContext';
import { toast } from 'react-hot-toast';

const EnableExitNotificationsModal = ({ isOpen, onClose, initialEnabled = false, initialChatId = '' }) => {
  const { currentPortfolioId } = usePortfolioContext();
  const [enabled, setEnabled] = useState(initialEnabled);
  const [chatId, setChatId] = useState(initialChatId || '');
  const [saving, setSaving] = useState(false);
  const [updateSettings] = useMutation(UPDATE_NOTIFICATION_SETTINGS);

  useEffect(() => {
    if (!isOpen) return;
    // Initialize state from provided props when opening
    setEnabled(!!initialEnabled);
    setChatId(initialChatId || '');
    const handleEscKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEscKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', handleEscKey); document.body.style.overflow = prev || 'unset'; };
  }, [isOpen, onClose, initialEnabled, initialChatId]);

  const handleSave = async () => {
    if (!currentPortfolioId) { toast.error('No portfolio selected'); return; }
    if (enabled && !chatId.trim()) { toast.error('Enter Telegram Chat ID'); return; }
    setSaving(true);
    try {
      const { data } = await updateSettings({
        variables: {
          input: {
            portfolioID: currentPortfolioId,
          telegramEnabled: enabled,
          telegramChatID: chatId.trim(),
          exitLevelNotifications: enabled ? true : false,
            priceAlerts: false,
            portfolioSummaryNotifications: false,
            notificationFrequency: 1,
          },
        },
      });
      if (data?.updateNotificationSettings?.success) {
        toast.success('Exit level notifications enabled');
        try {
          const evt = new CustomEvent('notifications:updated', { detail: { portfolioId: currentPortfolioId } });
          window.dispatchEvent(evt);
        } catch (_) {}
        onClose();
      } else {
        toast.error(data?.updateNotificationSettings?.error || 'Failed to enable notifications');
      }
    } catch (e) {
      toast.error(e.message || 'Failed to enable notifications');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-start justify-center overflow-y-auto">
      <div className="bg-white dark:bg-gray-800 rounded-md shadow-lg w-11/12 md:w-2/3 lg:w-1/2 mt-16 p-4">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Enable Exit Level Notifications</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">✕</button>
        </div>

        <div className="space-y-3 text-sm text-gray-700 dark:text-gray-300">
          <p>Exit level notifications are delivered via Telegram.</p>
          <ol className="list-decimal list-inside space-y-2">
            <li>
              Open the Telegram bot and send a message: {' '}
              <a href="https://t.me/moonready_bot" target="_blank" rel="noreferrer" className="text-blue-600 dark:text-blue-400 underline">@moonready_bot</a>
            </li>
            <li>
              Enter the Chat ID from the bot's reply below and click Save.
            </li>
          </ol>

          <div className="flex items-center gap-2">
            <input
              id="enable-tg"
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(!!e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="enable-tg" className="select-none">Enable Telegram notifications</label>
          </div>

          <div className="mt-2">
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Telegram Chat ID</label>
            <input
              type="text"
              value={chatId}
              onChange={(e) => setChatId(e.target.value)}
              placeholder="e.g. 123456789"
              disabled={!enabled}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100 disabled:cursor-not-allowed dark:disabled:bg-gray-600"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-3 py-1.5 rounded-md bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="px-3 py-1.5 rounded-md bg-blue-600 text-white disabled:opacity-60">{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
};

export default EnableExitNotificationsModal;


